import React, { useMemo, useState } from 'react';
import { Check, MessageCircle, Rotate3d, CalendarDays, Sparkles } from 'lucide-react';
import VisorWeb from './VisorWeb';
import SinServicio from './SinServicio';
import useRecorridos360 from './useRecorridos360';
import { PRECIO_MENSUAL, PRECIO_ALTA, EJEMPLO_360, linkContratar } from './recorrido360.config';

/* Pestaña "Recorrido 360°" de la cámara.
   - Si la obra tiene recorridos cargados (recorridos360.data.js, por feedID): los muestra adentro, con secciones por
     obra (una misma cámara puede tener la obra de hoy y las que vengan) y, dentro de cada obra, las fechas de captura.
   - Si no: explica el servicio y el botón "Contratar servicio" abre el WhatsApp de ventas con el pedido escrito.
   En los dos casos se ve el precio. */

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const fecha = (iso) => {
  if (!iso) return '';
  const [a, m, d] = iso.split('-').map(Number);
  return d ? `${d} ${MESES[m - 1]} ${a}` : `${MESES[m - 1]} ${a}`;
};

const Precio = ({ activo }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-5 md:p-6 grid sm:grid-cols-2 gap-4">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center shrink-0"><Rotate3d size={20} /></div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Servicio Recorrido 360°</div>
        <div className="text-2xl font-bold text-gray-800 leading-tight">{PRECIO_MENSUAL}<span className="text-base font-medium text-gray-500"> / mes</span></div>
        {activo && <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full"><Check size={11} />Activo en esta obra</div>}
      </div>
    </div>
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center shrink-0"><Sparkles size={18} /></div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Puesta en marcha</div>
        <div className="text-2xl font-bold text-gray-800 leading-tight">{PRECIO_ALTA}<span className="text-base font-medium text-gray-500"> única vez</span></div>
        <div className="text-xs text-gray-500 mt-1">Junto a cada cámara.</div>
      </div>
    </div>
  </div>
);

const BotonWhatsApp = ({ href, children, chico }) => (
  <a href={href} target="_blank" rel="noopener noreferrer"
    className={`inline-flex items-center justify-center gap-2 rounded-xl font-semibold text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-sm ${chico ? 'px-4 py-2 text-sm' : 'px-6 py-3 text-base'}`}>
    <MessageCircle size={chico ? 16 : 20} />{children}
  </a>
);

/** Tiene el servicio: secciones por obra y, dentro, las fechas de captura (la más nueva primero). */
const ConRecorridos = ({ obras, nombreObra, contratar }) => {
  const [obraId, setObraId] = useState(obras[0].id);
  const obra = obras.find((o) => o.id === obraId) || obras[0];
  const capturas = useMemo(() => [...obra.recorridos].sort((a, b) => (b.fecha || '').localeCompare(a.fecha || '')), [obra]);
  const [fechaSel, setFechaSel] = useState(null);
  const actual = capturas.find((r) => r.fecha === fechaSel) || capturas[0];

  return (
    <div className="space-y-5">
      <div className="bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3 text-sm text-blue-900 flex flex-wrap items-center justify-between gap-3">
        <span>¿Querés un recorrido nuevo de esta obra, sumar otra obra o una cámara más? <b>{PRECIO_MENSUAL}/mes</b> · {PRECIO_ALTA} única vez</span>
        <BotonWhatsApp href={contratar} chico>Hablar con ventas</BotonWhatsApp>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full"><Rotate3d size={13} />Recorrido 360° · {PRECIO_MENSUAL}/mes</div>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold text-gray-800 leading-tight">{obra.titulo || nombreObra}</h2>
          {actual.fecha && <div className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-800 bg-white border border-gray-200 rounded-full px-3 py-1"><CalendarDays size={15} className="text-blue-600" />Recorrido del {fecha(actual.fecha)}{actual.detalle ? ` · ${actual.detalle}` : ''}</div>}
          <p className="text-gray-600 text-sm mt-2">Caminá la obra por dentro como si estuvieras ahí. Tocá los puntos del piso para avanzar y medí desde el visor.</p>
        </div>
      </div>

      {obras.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {obras.map((o) => (
            <button key={o.id} type="button" onClick={() => { setObraId(o.id); setFechaSel(null); }}
              className={`px-4 py-2 rounded-full text-sm font-medium border whitespace-nowrap ${o.id === obra.id ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}>
              {o.titulo}
            </button>
          ))}
        </div>
      )}

      {capturas.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-1.5 text-gray-500"><CalendarDays size={15} />Recorridos por fecha:</span>
          {capturas.map((r) => (
            <button key={r.fecha + (r.url || r.video)} type="button" onClick={() => setFechaSel(r.fecha)}
              className={`px-3 py-1 rounded-full border text-sm ${r === actual ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}>
              {fecha(r.fecha)}{r.detalle ? ` · ${r.detalle}` : ''}
            </button>
          ))}
        </div>
      )}

      <VisorWeb key={actual.url || actual.video} url={actual.url} video={actual.video} titulo={obra.titulo || nombreObra} />

      <Precio activo />
    </div>
  );
};

/** No tiene el servicio: qué es, ejemplo, precio y botón de contratar por WhatsApp. */
const SinRecorridos = ({ contratar }) => (
  <SinServicio
    Icono={Rotate3d} etiqueta="Recorrido 360°" tipo="360"
    titulo="Esta obra todavía no tiene recorrido 360°"
    texto="Un técnico de Lisual recorre la obra con una cámara 360 y la publicamos acá, piso por piso, para caminarla y medir desde el celular o la computadora."
    contratarTexto="Contratar recorrido 360°" contratarHref={contratar}
    precio={`${PRECIO_MENSUAL} por mes`} detallePrecio={`+ ${PRECIO_ALTA} por única vez, junto a cada cámara`}
    ejemploUrl={EJEMPLO_360} ejemploNombre="FAE Estructuras, Calle Salta"
    beneficios={['Recorrido interactivo tipo Matterport', 'Medir distancias desde el visor', 'Una captura nueva en cada etapa, con su fecha', 'Link para compartir con compradores e inversores']}
  />
);

export default function Recorrido360({ user = {}, customerInfo, obrasDemo }) {
  const encontradas = useRecorridos360({ feedID: user.feedID, identifier: user.identifier });
  const obras = obrasDemo || encontradas;
  const contratar = linkContratar({ obra: user.Name, empresa: customerInfo?.name, feedID: user.feedID });
  return obras && obras.length
    ? <ConRecorridos obras={obras} nombreObra={user.Name} contratar={contratar} />
    : <SinRecorridos contratar={contratar} />;
}
